import axios from 'axios';
import createAuthRefreshInterceptor from 'axios-auth-refresh';

import {
  getGxTokenFromStorage,
  getTripTokenFromStorage,
  setTripTokenToStorage
} from '@/services/storage';

import type {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig
} from 'axios';

interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

const MAX_RETRY_ATTEMPTS = 3;

const baseURL = import.meta.env.VITE_REACT_APP_API_ENDPOINT;
const dataURL = import.meta.env.VITE_GX_DATA_ENDPOINT;
const gxURL = import.meta.env.VITE_GX_API_ENDPOINT;
const internalURL = import.meta.env.VITE_GX_INTERNAL_ENDPOINT;
const gxGoToken = import.meta.env.VITE_GX_GO_TOKEN;

const axiosGxInstance = axios.create({ baseURL: gxURL });
const unauthenticatedGxInstance = axios.create({ baseURL: gxURL });
const axiosGoInstance = axios.create({ baseURL, headers: { 'x-gx-go-token': gxGoToken } });
const axiosDataInstance = axios.create({
  baseURL: dataURL,
  headers: { 'x-gx-go-token': gxGoToken }
});
const axiosInternalInstance = axios.create({
  baseURL: internalURL,
  headers: { 'x-gx-go-token': gxGoToken }
});

const instances = [axiosGoInstance, axiosDataInstance, axiosInternalInstance, axiosGxInstance];

function attachRequestInterceptor(instance: AxiosInstance) {
  instance.interceptors.request.use((config: CustomAxiosRequestConfig) => {
    const tripToken = getTripTokenFromStorage();
    if (tripToken && config.headers) {
      config.headers.Authorization = `Bearer ${tripToken}`;
    }
    return config;
  });
}
export const getNewTripToken = async (gxToken: string): Promise<string | null> => {
  let attempts = 0;
  let isForbidden = false;

  while (attempts < MAX_RETRY_ATTEMPTS) {
    try {
      const url = `/token?token=${encodeURIComponent(gxToken)}`;
      const response = await axiosInternalInstance.get(url, {
        skipAuthRefresh: true
      } as AxiosRequestConfig);
      if (response.data?.token) {
        setTripTokenToStorage(response.data.token);
        return response.data.token;
      }
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        isForbidden = true;
        break;
      }
      console.error(`Token refresh attempt ${attempts + 1} failed:`, error);
    }

    attempts += 1;
  }

  if (isForbidden) {
    globalThis.location.href = '/expired';
  } else {
    globalThis.location.href = '/notfound';
  }

  return null;
};

function attachAuthRefreshInterceptor(instance: AxiosInstance) {
  const refreshLogic = async (failedRequest: any) => {
    const gxToken = getGxTokenFromStorage();

    if (!gxToken) {
      globalThis.location.href = '/expired';
      throw new Error('No gxToken to refresh');
    }

    const newToken = await getNewTripToken(gxToken);

    if (!newToken) {
      throw new Error('Could not refresh trip token');
    }

    failedRequest.response.config.headers.Authorization = `Bearer ${newToken}`;
  };

  createAuthRefreshInterceptor(instance, refreshLogic, {
    statusCodes: [401],
    pauseInstanceWhileRefreshing: true
  });
}
function attachFallbackResponseInterceptor(instance: AxiosInstance) {
  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    error => {
      const isLogin = error.config?.url?.includes('/login');
      const gxToken = getGxTokenFromStorage();

      if (!gxToken && !isLogin) {
        if (error.response?.status === 401 || error.response?.status === 403) {
          globalThis.location.href = '/expired';
        } else if (error.response?.status !== 404) {
          globalThis.location.href = '/notfound';
        }
      }

      return Promise.reject(error);
    }
  );
}

function setupInterceptors(instances: AxiosInstance[]) {
  for (const instance of instances) {
    attachRequestInterceptor(instance);
    attachAuthRefreshInterceptor(instance);
    attachFallbackResponseInterceptor(instance);
  }
}

setupInterceptors(instances);

const httpInstances = {
  axiosGoInstance,
  axiosDataInstance,
  axiosInternalInstance,
  axiosGxInstance,
  unauthenticatedGxInstance
};

export default httpInstances;
